import { useEffect, useState } from "react";
import { toast } from "sonner";

import ChevronDownIcon from "../../assets/svg/chevron-down.svg";
import {
  getFollowUpAdvanceAmountFormData,
  recordFollowUpAdvanceAmountReceived,
} from "../../api/followUpDiscussion";

function CurrencyAmountField({
  label,
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div
      className="flex flex-col items-start gap-1"
      style={{ flex: "1 1 0", minWidth: 0 }}
    >
      <label
        className="flex items-center text-[12px] font-normal leading-5 text-[#222222]"
        style={{
          fontFamily: "Inter, sans-serif",
          fontFeatureSettings: "'ss01' on, 'cv01' on",
        }}
      >
        {label}
      </label>
      <div
        className="flex flex-row justify-between items-center w-full h-9 px-0 gap-2.5"
        style={{
          background: "#FFFFFF",
          border: "1px solid #EEEEEE",
          borderRadius: "6px",
          boxSizing: "border-box",
        }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => !readOnly && onChange(e.target.value)}
          readOnly={readOnly}
          placeholder="00.00"
          className="flex-1 min-w-0 h-full pl-2.5 pr-0 py-0 bg-transparent text-[12px] font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] border-0 outline-none focus:ring-0 disabled:bg-transparent disabled:cursor-default"
          style={{
            fontFamily: "Inter, sans-serif",
            fontFeatureSettings: "'ss01' on, 'cv01' on",
          }}
        />
        <div
          className="flex flex-row justify-center items-center shrink-0 px-2.5 py-2 gap-2.5 h-9 min-w-[76px]"
          style={{
            background: "#F7F7F7",
            borderRadius: "0 6px 6px 0",
          }}
        >
          <span
            className="text-[12px] font-normal leading-5 text-[#6D6D6D]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontFeatureSettings: "'ss01' on, 'cv01' on",
            }}
          >
            Rs(₹)
          </span>
          <img
            src={ChevronDownIcon}
            alt=""
            className="w-4 h-4 shrink-0"
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

function formatAmount(num: number | null | undefined): string {
  if (num == null || !Number.isFinite(num) || num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseAmount(s: string): number {
  const cleaned = String(s || "").replace(/[^\d.]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

type FollowUpAdvanceAmountFromClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export const FOLLOW_UP_ADVANCE_AMOUNT_FROM_CLIENT_FORM_ID =
  "follow-up-advance-amount-from-client-form";

export default function FollowUpAdvanceAmountFromClientView({
  leadId,
  onSuccess,
}: FollowUpAdvanceAmountFromClientViewProps) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [collectedAdvanceAmount, setCollectedAdvanceAmount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getFollowUpAdvanceAmountFormData(leadId)
      .then((data) => {
        if (cancelled) return;
        setAdvanceAmount(formatAmount(data.advanceAmount));
        setCollectedAdvanceAmount(formatAmount(data.collectedAdvanceAmount));
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load advance amount details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const advanceNum = parseAmount(advanceAmount);
  const collectedNum = parseAmount(collectedAdvanceAmount);
  const collectedExceedsAdvance =
    collectedNum > 0 && advanceNum > 0 && collectedNum > advanceNum;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (collectedNum <= 0) {
      toast.error(
        "Collected advance amount must be greater than 0. Use Progress to skip this step."
      );
      return;
    }
    if (collectedExceedsAdvance) {
      toast.error(
        "Collected advance amount cannot be greater than the advance amount."
      );
      return;
    }
    setSaving(true);
    try {
      await recordFollowUpAdvanceAmountReceived(leadId, {
        collectedAdvanceAmount: collectedNum,
      });
      toast.success("Advance amount from client recorded successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save advance amount"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={FOLLOW_UP_ADVANCE_AMOUNT_FROM_CLIENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col items-start w-full max-w-[1085px] gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {saving && <p className="text-xs text-[#6D6D6D]">Saving...</p>}

      <div
        className="flex flex-col sm:flex-row items-stretch sm:items-start gap-5 w-full"
      >
        <CurrencyAmountField
          label="Advance Amount (Fetched)"
          value={advanceAmount}
          onChange={() => {}}
          readOnly
        />
        <div className="flex flex-col gap-1" style={{ flex: "1 1 0", minWidth: 0 }}>
          <CurrencyAmountField
            label="Collected Advance Amount"
            value={collectedAdvanceAmount}
            onChange={setCollectedAdvanceAmount}
          />
          {collectedExceedsAdvance && (
            <p
              className="text-[11px] text-red-500 leading-4"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Collected advance amount cannot be greater than the advance amount.
            </p>
          )}
        </div>
      </div>
    </form>
  );
}
